// Section 6: Sale Rates & Revenue
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { IndianRupee } from "lucide-react";
import { IndianNumberInput } from "@/components/IndianNumberInput";
import { REQ } from "../utils";

export const RevenueSaleRateSection = ({
  formData,
  setFormData,
  handleChange,
  handleSelectChange,
  getInputClass,
  hasFieldError
}) => {
  return (
    <Card className="rounded-sm shadow-sm border-l-4 border-l-teal-400" data-testid="quick-revenue">
      <CardHeader className="pb-3 bg-teal-100 rounded-t-xl border-b border-teal-200">
        <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2 text-teal-900">
          <IndianRupee className="h-5 w-5 text-muted-foreground" />
          6. Sale Rates & Revenue
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground">
          Set different sale rates for each property type
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Sale Rate - Flats: Hidden for Commercial, visible for Residential and Mixed */}
          {formData.projectType !== "commercial" && (
            <div className="space-y-2">
              <Label htmlFor="q-saleRateFlats">Sale Rate - Flats (₹/sft) *</Label>
              <IndianNumberInput
                id="q-saleRateFlats"
                name="saleRateFlats"
                value={formData.saleRateFlats || formData.saleRatePerSqft || ""}
                onChange={handleChange}
                placeholder="15,000"
                className={getInputClass("saleRateFlats", REQ)}
                data-testid="quick-sale-rate-flats"
              />
              {hasFieldError("saleRateFlats") && <p className="text-xs text-red-500">Required</p>}
            </div>
          )}
          {/* Sale Rate - Offices: Only for Commercial or Mixed Use */}
          {(formData.projectType === "commercial" || formData.projectType === "mixed") && (
            <div className="space-y-2">
              <Label htmlFor="q-saleRateOffices">
                Sale Rate - Offices (₹/sft) {formData.projectType === "commercial" ? "*" : ""}
              </Label>
              <IndianNumberInput
                id="q-saleRateOffices"
                name="saleRateOffices"
                value={formData.saleRateOffices || ""}
                onChange={handleChange}
                placeholder="12,000"
                className={formData.projectType === "commercial" ? getInputClass("saleRateOffices", REQ) : "rounded-sm"}
                data-testid="quick-sale-rate-offices"
              />
              {formData.projectType === "commercial" && hasFieldError("saleRateOffices") && (
                <p className="text-xs text-red-500">Required</p>
              )}
            </div>
          )}
          {/* Sale Rate - Shops: Only for Commercial or Mixed Use */}
          {(formData.projectType === "commercial" || formData.projectType === "mixed") && (
            <div className="space-y-2">
              <Label htmlFor="q-saleRateShops">
                Sale Rate - Shops (₹/sft) {formData.projectType === "commercial" ? "*" : ""}
              </Label>
              <IndianNumberInput
                id="q-saleRateShops"
                name="saleRateShops"
                value={formData.saleRateShops || ""}
                onChange={handleChange}
                placeholder="18,000"
                className={formData.projectType === "commercial" ? getInputClass("saleRateShops", REQ) : "rounded-sm"}
                data-testid="quick-sale-rate-shops"
              />
              {formData.projectType === "commercial" && hasFieldError("saleRateShops") && (
                <p className="text-xs text-red-500">Required</p>
              )}
            </div>
          )}
        </div>
        
        <Separator />
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              Marketing Expenses *
              <span className="text-xs text-muted-foreground">(Required)</span>
            </Label>
            <div className="flex gap-2">
              <Select
                value={formData.marketingExpenseMode || "percent"}
                onValueChange={(value) => handleSelectChange("marketingExpenseMode", value)}
              >
                <SelectTrigger className="rounded-sm w-32" data-testid="quick-marketing-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage</SelectItem>
                  <SelectItem value="per_sft">Per Sft</SelectItem>
                </SelectContent>
              </Select>
              
              {(formData.marketingExpenseMode || "percent") === "percent" ? (
                <Select
                  value={formData.marketingExpensePercent || "3"}
                  onValueChange={(value) => {
                    if (value === "custom") {
                      handleSelectChange("marketingExpenseType", "custom");
                    } else {
                      setFormData({
                        ...formData,
                        marketingExpenseType: "percent",
                        marketingExpensePercent: value
                      });
                    }
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="quick-marketing-percent">
                    <SelectValue placeholder="Select %" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1%</SelectItem>
                    <SelectItem value="2">2%</SelectItem>
                    <SelectItem value="3">3%</SelectItem>
                    <SelectItem value="4">4%</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  name="marketingCostPerSqft"
                  type="number"
                  value={formData.marketingCostPerSqft || ""}
                  onChange={handleChange}
                  placeholder="₹/sft"
                  className="rounded-sm"
                  data-testid="quick-marketing-sft"
                />
              )}
              
              {formData.marketingExpenseType === "custom" && (formData.marketingExpenseMode || "percent") === "percent" && (
                <Input
                  name="marketingExpensePercent"
                  type="number"
                  value={formData.marketingExpensePercent || ""}
                  onChange={handleChange}
                  placeholder="%"
                  className="rounded-sm w-24"
                  data-testid="quick-marketing-custom"
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {(formData.marketingExpenseMode || "percent") === "percent" 
                ? "% of (Free Sale Area × Sale Rate)" 
                : "Amount per sft of sale area"}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="q-miscExpPerSqft">Misc Expenses (₹/sft)</Label>
            <Input
              id="q-miscExpPerSqft"
              name="miscExpPerSqft"
              type="number"
              value={formData.miscExpPerSqft}
              onChange={handleChange}
              placeholder="100"
              className="rounded-sm"
              data-testid="quick-misc"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
